import { create } from 'zustand';
import { getBoard, updateBoard } from '../api/plannerApi';
import { isPayable } from '../types/board';
import type { Attachment, Board, Guest, Item, Payment, Status } from '../types/board';

const rid = (prefix: string) => `${prefix}_${crypto.randomUUID()}`;

const clone = (b: Board) => JSON.parse(JSON.stringify(b)) as Board;

// Automation: first payment books an in-progress item; full payment marks it paid.
// Non-payable statuses (backlog/todo) never auto-advance.
export function advanceStatus(status: Status, paid: number, expectedPrice: number): Status {
  if (!isPayable(status)) return status;
  if (paid > 0 && status === 'inprogress') status = 'booked';
  if (expectedPrice > 0 && paid >= expectedPrice) status = 'paid';
  return status;
}

// ID-based merge for concurrent edits (ported from the moodboard app): entities
// I changed since my last synced snapshot win; entities I didn't touch follow
// the server's version. Server deletion of an entity I edited also wins.
export function mergeBoard(base: unknown, mine: unknown, snap: unknown): any {
  if (Array.isArray(mine)) {
    const bArr = (Array.isArray(base) ? base : []) as Array<{ id?: string }>;
    const sArr = (Array.isArray(snap) ? snap : []) as Array<{ id?: string }>;
    const ids = [
      ...new Set(
        [...bArr.map((e) => e?.id), ...mine.map((e) => (e as { id?: string }).id)].filter(Boolean),
      ),
    ];
    const out: any[] = [];
    for (const id of ids) {
      const b = bArr.find((e) => e?.id === id);
      const m = mine.find((e) => (e as { id?: string }).id === id);
      const s = sArr.find((e) => e?.id === id);
      if (m && s && JSON.stringify(m) === JSON.stringify(s)) {
        if (b) out.push(b); // untouched by me → server wins
        continue;
      }
      if (m && s) {
        if (!b) continue; // deleted on server → gone
        out.push(mergeBoard(b, m, s)); // edited by me → deep merge
        continue;
      }
      if (m) { out.push(m); continue; } // new by me
      if (!s && b) out.push(b); // only known to server
    }
    return out;
  }
  if (mine && typeof mine === 'object') {
    const baseObj = (base && typeof base === 'object' ? base : {}) as Record<string, unknown>;
    const snapObj = (snap && typeof snap === 'object' ? snap : {}) as Record<string, unknown>;
    const mineObj = mine as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of new Set([...Object.keys(baseObj), ...Object.keys(mineObj)])) {
      out[k] = mergeBoard(baseObj[k], mineObj[k], snapObj[k]);
    }
    return out;
  }
  return mine === snap ? (base === undefined ? mine : base) : mine;
}

export const emptyBoard: Board = { title: 'Our Wedding Budget', categories: [], items: [], guests: [] };

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface BoardState {
  editToken: string;
  board: Board;
  updatedAt: number;
  lastSynced: Board | null;
  prevBoard: Board | null;
  loading: boolean;
  error: string | null;
  saveStatus: SaveStatus;
  saveTimer: ReturnType<typeof setTimeout> | null;
  load: (editToken: string) => Promise<void>;
  setBoard: (fn: (b: Board) => Board) => void;
  setTitle: (title: string) => void;
  snapshot: () => void;
  undo: () => void;

  addCategory: (name: string) => void;
  renameCategory: (id: string, name: string) => void;
  deleteCategory: (id: string) => void;

  addItem: (status?: Status, title?: string) => void;
  updateItem: (id: string, patch: Partial<Item>) => void;
  deleteItem: (id: string) => void;
  toggleItemCategory: (itemId: string, categoryId: string) => void;

  addPayment: (itemId: string, payment: Omit<Payment, 'id'>) => void;
  removePayment: (itemId: string, paymentId: string) => void;
  addAttachment: (itemId: string, url: string) => void;
  removeAttachment: (itemId: string, attachmentId: string) => void;

  addGuest: () => void;
  updateGuest: (id: string, patch: Partial<Guest>) => void;
  removeGuest: (id: string) => void;
}

export const useBoardStore = create<BoardState>((set, get) => ({
  editToken: '',
  board: emptyBoard,
  updatedAt: 0,
  lastSynced: null,
  prevBoard: null,
  loading: true,
  error: null,
  saveStatus: 'idle',
  saveTimer: null,

  load: async (editToken) => {
    set({ editToken, loading: true, error: null });
    try {
      const result = await getBoard(editToken);
      set({
        board: result.board,
        updatedAt: result.updatedAt,
        lastSynced: clone(result.board),
        loading: false,
      });
    } catch {
      set({ error: 'Could not load planner board.', loading: false });
    }
  },

  setBoard: (fn) => {
    const board = fn(get().board);
    set({ board, saveStatus: 'saving' });
    const existing = get().saveTimer;
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => save(set, get), 600);
    set({ saveTimer: timer });
  },

  setTitle: (title) => get().setBoard((b) => ({ ...b, title })),

  snapshot: () => set({ prevBoard: clone(get().board) }),

  undo: () => {
    const prev = get().prevBoard;
    if (!prev) return;
    get().setBoard(() => prev);
    set({ prevBoard: null });
  },

  addCategory: (name) =>
    get().setBoard((b) => ({ ...b, categories: [...b.categories, { id: rid('cat'), name }] })),
  renameCategory: (id, name) =>
    get().setBoard((b) => ({ ...b, categories: b.categories.map((c) => (c.id === id ? { ...c, name } : c)) })),
  deleteCategory: (id) => {
    get().snapshot();
    get().setBoard((b) => ({
      ...b,
      categories: b.categories.filter((c) => c.id !== id),
      items: b.items.map((i) => ({ ...i, categoryIds: i.categoryIds.filter((cid) => cid !== id) })),
    }));
  },

  addItem: (status = 'todo', title = 'New item') =>
    get().setBoard((b) => ({
      ...b,
      items: [
        ...b.items,
        {
          id: rid('item'),
          title,
          categoryIds: [],
          expectedPrice: 0,
          payments: [],
          vendor: '',
          dueDate: '',
          priority: false,
          notes: '',
          attachments: [],
          status,
        },
      ],
    })),
  updateItem: (id, patch) =>
    get().setBoard((b) => ({ ...b, items: b.items.map((i) => (i.id === id ? { ...i, ...patch } : i)) })),
  deleteItem: (id) => {
    get().snapshot();
    get().setBoard((b) => ({ ...b, items: b.items.filter((i) => i.id !== id) }));
  },
  toggleItemCategory: (itemId, categoryId) =>
    get().setBoard((b) => ({
      ...b,
      items: b.items.map((i) =>
        i.id === itemId
          ? {
              ...i,
              categoryIds: i.categoryIds.includes(categoryId)
                ? i.categoryIds.filter((c) => c !== categoryId)
                : [...i.categoryIds, categoryId],
            }
          : i,
      ),
    })),

  addPayment: (itemId, payment) =>
    get().setBoard((b) => ({
      ...b,
      items: b.items.map((i) => {
        if (i.id !== itemId) return i;
        const payments = [...i.payments, { ...payment, id: rid('pay') }];
        const paid = payments.reduce((a, p) => a + (p.amount || 0), 0);
        return { ...i, payments, status: advanceStatus(i.status, paid, i.expectedPrice) };
      }),
    })),
  removePayment: (itemId, paymentId) => {
    get().snapshot();
    get().setBoard((b) => ({
      ...b,
      items: b.items.map((i) =>
        i.id === itemId ? { ...i, payments: i.payments.filter((p) => p.id !== paymentId) } : i,
      ),
    }));
  },
  addAttachment: (itemId, url) =>
    get().setBoard((b) => ({
      ...b,
      items: b.items.map((i) =>
        i.id === itemId ? { ...i, attachments: [...i.attachments, { id: rid('att'), url } as Attachment] } : i,
      ),
    })),
  removeAttachment: (itemId, attachmentId) => {
    get().snapshot();
    get().setBoard((b) => ({
      ...b,
      items: b.items.map((i) =>
        i.id === itemId ? { ...i, attachments: i.attachments.filter((a) => a.id !== attachmentId) } : i,
      ),
    }));
  },

  addGuest: () =>
    get().setBoard((b) => ({
      ...b,
      guests: [
        ...b.guests,
        {
          id: rid('guest'),
          name: 'New guest',
          side: 'both',
          rsvp: 'pending',
          plusOne: false,
          checkedIn: false,
          notes: '',
        } as Guest,
      ],
    })),
  updateGuest: (id, patch) =>
    get().setBoard((b) => ({ ...b, guests: b.guests.map((g) => (g.id === id ? { ...g, ...patch } : g)) })),
  removeGuest: (id) => {
    get().snapshot();
    get().setBoard((b) => ({ ...b, guests: b.guests.filter((g) => g.id !== id) }));
  },
}));

async function save(set: (partial: Partial<BoardState>) => void, get: () => BoardState) {
  const { editToken, board, updatedAt, lastSynced } = get();
  // Snapshot the sync baseline before any await: a concurrent save may advance
  // the timestamps mid-flight, and merging stale `latest` against them reverts saves.
  const baseUpdatedAt = updatedAt;
  const baseSnapshot = lastSynced;
  const sent = clone(board);
  let out: Board = sent;
  try {
    const latest = await getBoard(editToken);
    if (latest.updatedAt !== baseUpdatedAt) out = mergeBoard(latest.board, out, baseSnapshot);
    const result = await updateBoard(editToken, out, latest.updatedAt);
    set({ saveStatus: 'saved', updatedAt: result.updatedAt, lastSynced: clone(out) });
    // Pull foreign changes into state without clobbering edits made mid-flight.
    const current = get().board;
    const mergedNow = mergeBoard(out, current, sent);
    if (JSON.stringify(mergedNow) !== JSON.stringify(current)) set({ board: mergedNow });
  } catch (e) {
    const conflict = (e as { conflict?: { board: Board; latestUpdatedAt: number } }).conflict;
    if (conflict) {
      // Retry once against the server's latest timestamp; unchanged-by-me
      // entities follow the server, my edits win.
      try {
        const retried = mergeBoard(conflict.board, out, baseSnapshot);
        const retry = await updateBoard(editToken, retried, conflict.latestUpdatedAt);
        set({ saveStatus: 'saved', updatedAt: retry.updatedAt, lastSynced: clone(retried) });
        return;
      } catch {
        set({ saveStatus: 'error' });
        return;
      }
    }
    set({ saveStatus: 'error' });
  }
}

export { rid };
